// @ts-strict-ignore
import React, { FC, useState, useEffect } from 'react'
import { Hotspot } from '../../types'
import useKrpano from 'react-krpano-hooks'
import { getStyleProperty, waitForSomething } from '../../../platform'
import { generateImageUrl } from '../../../../common_lib/utils'
import { KRPanoCoords, KRPanoImageFOV, ImageSize } from '../../../../common_lib/types'
import { GetScreensShowResponse, MediaItem } from 'mytours-read-api-typescript'
import panoramaInfoicon from '../../../../assets/panorama-info-icon.svg'

interface PanoramaProps {
	hotspots: Hotspot[]
	imageType: GetScreensShowResponse.ImageTypeEnum
	panoramaImage: MediaItem
}

const MAX_FOV = 120
const MIN_FOV = 60
const FOV_STEP = 5
const DEFAULT_HOTSPOT_SIZE = 25

const Panorama: FC<PanoramaProps> = ({ imageType, panoramaImage, hotspots }) => {
	const [krpano, setKrpano] = useState<any>()
	const [fov, setFov] = useState<KRPanoImageFOV>()
	const [imageSize, setImageSize] = useState<ImageSize>()

	// TODO: use env or global variable instead
	const cssLink = document.querySelector('link[href$="index.css"]') as HTMLLinkElement
	const cdnPath = cssLink?.href.replace(/\/index\.css/, '') || ''

	const {
		krpanoState: { isLoaded },
		getKrpano,
		containerRef,
	} = useKrpano({
		scriptPath: cdnPath + '/krpano/krpano.js',
		embeddingParams: { xml: cdnPath + '/krpano/krpano_mobile.xml' },
		height: '100%',
		width: '100%',
	})

	const zoomIn = (krpano: any) => {
		const fov = krpano.get('view.fov')
		if (fov > MIN_FOV) {
			krpano.call(`zoomto(${fov - FOV_STEP})`)
		}
	}

	const zoomOut = (krpano: any) => {
		const fov = krpano.get('view.fov')
		if (fov < MAX_FOV) {
			krpano.call(`zoomto(${fov + FOV_STEP})`)
		}
	}

	const initControlButtons = (krpano: any) => {
		krpano.actions.zoomin = () => {
			zoomIn(krpano)
		}
		krpano.actions.zoomout = () => {
			zoomOut(krpano)
		}
		krpano.set('plugin[zoomin].html', `<div class="pano-button"><i class="far fa-plus" /><div>`)
		krpano.set('plugin[zoomout].html', `<div class="pano-button"><i class="far fa-minus" /><div>`)
	}

	useEffect(() => {
		if (isLoaded) {
			const krpano = getKrpano('global')
			initControlButtons(krpano)
			setKrpano(krpano)
		}
	}, [isLoaded])

	const convertXYtoKRpanoCoords = (x: number, y: number): KRPanoCoords => ({
		ath: (x / imageSize.w - 0.5) * fov.h,
		atv: (y / imageSize.h - 0.5) * fov.v,
	})

	const createHotspot = (hotspot: Hotspot): void => {
		const { x, y, iconWidth, iconHeight, icon, url, text, id, pitch, yaw, onClick } = hotspot

		const width = icon ? iconWidth : DEFAULT_HOTSPOT_SIZE
		const height = icon ? iconHeight : DEFAULT_HOTSPOT_SIZE

		const imageUrl = icon
			? generateImageUrl(icon.file, {
					width: iconWidth,
					height: iconHeight,
					crop: true,
			  })
			: panoramaInfoicon
		const offsetTop = height / 2 + 25

		const handleClick = url ? () => window.open(url, '_blank').focus() : onClick

		krpano.call(`addhotspot(hotspot_${id})`)
		krpano.call(`addplugin(tooltip_${id})`)
		krpano.set(`hotspot[hotspot_${id}].url`, imageUrl)
		krpano.set(`hotspot[hotspot_${id}].width`, width)
		krpano.set(`hotspot[hotspot_${id}].height`, height)
		krpano.set(`hotspot[hotspot_${id}].keep`, true)
		// Use - here because in KRPano coordinates are in different direction (compatibility with Pannellum)
		if (imageType === GetScreensShowResponse.ImageTypeEnum.Panorama) {
			krpano.set(`hotspot[hotspot_${id}].atv`, -pitch)
			krpano.set(`hotspot[hotspot_${id}].ath`, yaw)
		} else {
			const { ath, atv } = convertXYtoKRpanoCoords(x, y)
			krpano.set(`hotspot[hotspot_${id}].atv`, atv)
			krpano.set(`hotspot[hotspot_${id}].ath`, ath)
		}
		krpano.set(`hotspot[hotspot_${id}].onclick`, handleClick)
		krpano.set(`hotspot[hotspot_${id}].onhover`, () => krpano.set(`plugin[tooltip_${id}].visible`, true))
		krpano.set(`hotspot[hotspot_${id}].onout`, () =>
			setTimeout(() => {
				krpano.set(`plugin[tooltip_${id}].visible`, false)
			}, 500),
		)

		krpano.set(`plugin[tooltip_${id}].type`, 'text')
		krpano.set(`plugin[tooltip_${id}].html`, text)
		krpano.set(`plugin[tooltip_${id}].bgalpha`, 0.8)
		krpano.set(`plugin[tooltip_${id}].parent`, `hotspot[hotspot_${id}]`)
		krpano.set(`plugin[tooltip_${id}].edge`, 'center')
		krpano.set(`plugin[tooltip_${id}].align`, 'center')
		krpano.set(`plugin[tooltip_${id}].bgcolor`, `#000`)
		krpano.set(`plugin[tooltip_${id}].y`, -offsetTop)
		krpano.set(
			`plugin[tooltip_${id}].css`,
			'padding: 5px 10px; color: white; text-align: center; font-family: var(--body-font-font-family), sans-serif; font-size: 1rem; z-index: 1000;',
		)
		krpano.set(`plugin[tooltip_${id}].visible`, false)
	}

	useEffect(() => {
		if (krpano && hotspots?.length > 0 && imageSize && fov) {
			hotspots.map(hs => createHotspot(hs))
		}
	}, [krpano, hotspots, imageSize, fov])

	const getImageSizeFromUrl = (url: string) => {
		const img = new Image()
		img.addEventListener('load', () => {
			setImageSize({ w: img.naturalWidth, h: img.naturalHeight })
		})
		img.src = `${url}?type=imageSize`
	}

	useEffect(() => {
		if (imageSize && imageType) {
			const h = imageType === GetScreensShowResponse.ImageTypeEnum.Panorama ? 360 : 1
			const v = (h * imageSize.h) / imageSize.w
			setFov({ h, v })
		}
	}, [imageSize, imageType])

	const setPanoramaImage = (): void => {
		const { file } = panoramaImage
		const isPanorama = imageType === GetScreensShowResponse.ImageTypeEnum.Panorama
		krpano.image.reset()
		krpano.image[isPanorama ? 'sphere' : 'flat'] = {
			url: `${file.original_url}&type=krpano`,
		}
		krpano.actions.loadpanoimage('MERGE')
		krpano.set('plugin[zoomin].visible', isPanorama ? true : false)
		krpano.set('plugin[zoomout].visible', isPanorama ? true : false)
		if (isPanorama) {
			krpano.call('zoomto(110)')
			krpano.set('view.fovmin', MIN_FOV)
			krpano.set('view.fovmax', MAX_FOV)
		} else {
			krpano.set('view.hlookat', 0)
			krpano.set('view.vlookat', 0)
			krpano.set('view.maxpixelzoom', 10)
			krpano.set('view.limitview', 'fullrange')
		}

		if (file.width && file.height) {
			setImageSize({ w: file.width, h: file.height })
		} else {
			getImageSizeFromUrl(file.original_url)
		}
	}

	useEffect(() => {
		if (krpano && panoramaImage) {
			waitForSomething(() => 'reset' in krpano.image).then(() => setPanoramaImage())
		}
	}, [panoramaImage, krpano])

	const bgColor = getStyleProperty('--background-color')

	return (
		<div
			ref={containerRef as any}
			className="panorama-viewer"
			style={{
				width: '100%',
				height: '100%',
				background: bgColor,
			}}
		/>
	)
}

export default Panorama
